// The device cache (PRD: chat feel): recent messages, the channel list, members and read positions, kept in
// IndexedDB so opening the app and switching channels draw at once, before the socket has said anything. The room
// stays the source of truth: on connect the Room sends its cached cursors and the room fills exactly the gap.
//
// Privacy: everything is filed under the signed-in user's id, and only one account's data is ever kept. Signing
// out, the session ending, or a different account signing in wipes it all; leaving a server (or a DM) wipes that
// part. Writes check the account first, so a tab still open after a sign-out elsewhere can't put anything back.
import type { Category, Channel, CustomEmoji, Me, Member, Message, NotifyLevel, ServerNotify, Sticker } from "@bloom/shared";

const DB_NAME = "bloom-cache";
const DB_VERSION = 1;
/** One record per server I'm in: the layout, my read positions, members, and the room's change counter. */
const SERVERS = "servers";
/** One record per conversation (channel, DM or thread): its newest messages. */
const STREAMS = "streams";
/** Messages kept per conversation. Enough for a screenful or three; the rest loads from the room as you scroll. */
export const CACHE_MESSAGES = 150;
/** localStorage: the signed-in account (the Me object), so the app can draw before /api/me answers. */
const ME_KEY = "bloom:me";

export interface CachedServer {
  key: string;
  categories: Category[];
  channels: Channel[];
  read: Record<string, number>;
  mentionCounts: Record<string, number>;
  notify: Record<string, NotifyLevel>;
  mutes?: Record<string, number>;
  /** DMs I closed in the sidebar: the seq each ended on then (#131). */
  dmClosed?: Record<string, number>;
  serverNotify?: ServerNotify;
  /** The server's custom emoji and stickers, so cached messages draw them before the room answers. */
  emojis?: CustomEmoji[];
  stickers?: Sticker[];
  /** The room's change counter from the last "ready"; sent back in hello so it re-sends what changed. */
  rev?: number;
}

export interface CachedStream {
  key: string;
  streamId: string;
  messages: Message[];
  /** Older messages exist above the first one kept. */
  more: boolean;
  /** Threads: the channel the parent message is in, so the thread goes when that channel does. */
  parent?: string;
  /**
   * The room's change counter these messages are up to date with. Per conversation, because two open tabs both
   * write here and one may be behind: a reconnect resumes from the lowest, so nothing stale is trusted.
   */
  rev?: number;
}

const serverKey = (userId: string, serverId: string) => `${userId}|${serverId}`;
/** The member list is its own record (it comes from the API, not the room, and is saved at other times). */
const membersKey = (userId: string, serverId: string) => `${userId}|${serverId}|members`;
const streamKey = (userId: string, serverId: string, streamId: string) => `${userId}|${serverId}|${streamId}`;
/** Every key under a prefix ("user|server|…"). "￿" sorts after any id character. */
const under = (prefix: string) => IDBKeyRange.bound(prefix, `${prefix}￿`);

// ---- The signed-in account ----

/** The account the cache (and the app's first paint) belongs to, as last confirmed by /api/me. */
export function cachedMe(): Me | undefined {
  try {
    const raw = localStorage.getItem(ME_KEY);
    return raw ? (JSON.parse(raw) as Me) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * /api/me answered. A different account than the one cached means someone else signed in on this device: wipe the
 * last one's messages before anything is drawn from them. Servers I'm no longer in are dropped too.
 */
export async function confirmMe(me: Me | null): Promise<void> {
  const before = cachedMe();
  if (!me) {
    if (before) await wipeAll();
    return;
  }
  if (before && before.id !== me.id) await wipeAll();
  try {
    localStorage.setItem(ME_KEY, JSON.stringify(me));
  } catch {
    // storage full or blocked: we just won't draw before /api/me next time
  }
  if (before && before.id === me.id) {
    const keep = new Set(me.servers.map((s) => s.id));
    for (const s of before.servers) if (!keep.has(s.id)) await wipeServer(me.id, s.id);
  }
}

/** Is this still the account the cache belongs to? (Another tab may have signed out or switched account.) */
const isOwner = (userId: string) => cachedMe()?.id === userId;

// ---- IndexedDB plumbing ----

let opening: Promise<IDBDatabase | null> | null = null;

/** The database, or null where IndexedDB isn't available (some private modes): the app then works without a cache. */
function db(): Promise<IDBDatabase | null> {
  opening ??= new Promise<IDBDatabase | null>((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const d = req.result;
        if (!d.objectStoreNames.contains(SERVERS)) d.createObjectStore(SERVERS, { keyPath: "key" });
        if (!d.objectStoreNames.contains(STREAMS)) d.createObjectStore(STREAMS, { keyPath: "key" });
      };
      req.onsuccess = () => {
        const d = req.result;
        // Another tab upgrading the schema: step aside so it isn't blocked.
        d.onversionchange = () => {
          d.close();
          opening = null;
        };
        resolve(d);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return opening;
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
}

function request<T>(req: IDBRequest<T>): Promise<T | undefined> {
  return new Promise((resolve) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(undefined);
  });
}

// ---- Reading and writing ----

/** Everything cached for one server: its record and its conversations. Null if nothing (or no IndexedDB). */
export async function loadServer(userId: string, serverId: string): Promise<{ server?: CachedServer; streams: CachedStream[]; members?: Member[] } | null> {
  if (!isOwner(userId)) return null;
  const d = await db();
  if (!d) return null;
  try {
    const tx = d.transaction([SERVERS, STREAMS], "readonly");
    const [server, members, streams] = await Promise.all([
      request(tx.objectStore(SERVERS).get(serverKey(userId, serverId)) as IDBRequest<CachedServer | undefined>),
      request(tx.objectStore(SERVERS).get(membersKey(userId, serverId)) as IDBRequest<{ members: Member[] } | undefined>),
      request(tx.objectStore(STREAMS).getAll(under(streamKey(userId, serverId, ""))) as IDBRequest<CachedStream[]>),
    ]);
    // Checked again: the account may have changed while we read.
    if (!isOwner(userId)) return null;
    return { server, streams: server ? (streams ?? []) : [], members: members?.members };
  } catch {
    return null;
  }
}

/** Saves the server record and any changed conversations; `drop` removes conversations (left DMs, closed threads' parents). */
export async function save(
  userId: string,
  serverId: string,
  change: { server?: Omit<CachedServer, "key">; streams?: Omit<CachedStream, "key">[]; drop?: string[] },
): Promise<void> {
  if (!isOwner(userId)) return;
  const d = await db();
  if (!d) return;
  try {
    const tx = d.transaction([SERVERS, STREAMS], "readwrite");
    if (change.server) tx.objectStore(SERVERS).put({ ...change.server, key: serverKey(userId, serverId) } satisfies CachedServer);
    const streams = tx.objectStore(STREAMS);
    for (const s of change.streams ?? []) streams.put({ ...s, key: streamKey(userId, serverId, s.streamId) } satisfies CachedStream);
    for (const id of change.drop ?? []) streams.delete(streamKey(userId, serverId, id));
    await done(tx);
  } catch {
    // quota or a closing database: the cache is only ever a head start
  }
}

/** The server's member list (from the API), for drawing names before it's fetched again. */
export async function saveMembers(userId: string, serverId: string, members: Member[]): Promise<void> {
  if (!isOwner(userId)) return;
  const d = await db();
  if (!d) return;
  try {
    const tx = d.transaction(SERVERS, "readwrite");
    tx.objectStore(SERVERS).put({ key: membersKey(userId, serverId), members });
    await done(tx);
  } catch {
    // ignore
  }
}

/** Forgets one server (I left, or was removed). */
export async function wipeServer(userId: string, serverId: string): Promise<void> {
  const d = await db();
  if (!d) return;
  try {
    const tx = d.transaction([SERVERS, STREAMS], "readwrite");
    tx.objectStore(SERVERS).delete(serverKey(userId, serverId));
    tx.objectStore(SERVERS).delete(membersKey(userId, serverId));
    tx.objectStore(STREAMS).delete(under(streamKey(userId, serverId, "")));
    await done(tx);
  } catch {
    // ignore
  }
}

/** Forgets everything: signing out, the session ending, or another account signing in. */
export async function wipeAll(): Promise<void> {
  try {
    localStorage.removeItem(ME_KEY);
  } catch {
    // ignore
  }
  const d = await db();
  if (!d) return;
  try {
    const tx = d.transaction([SERVERS, STREAMS], "readwrite");
    tx.objectStore(SERVERS).clear();
    tx.objectStore(STREAMS).clear();
    await done(tx);
  } catch {
    // ignore
  }
}
